import { useContext } from 'react'
import { useCookie } from 'react-use'
import { DateTime } from 'luxon'

import { type IBaseResponse, type IUser } from 'platform-core'
import { CoreRootService, RootContext } from 'platform-core/ui'
import {
  type IGetUserResponse,
  type IStatus,
  type IStatusResponse,
  type ISignUpEnabledResponse
} from '@/models'
import { submitRequest } from '@/util'
import {
  AUTHENTICATION_ERRORS,
  APP_DOMAIN,
  COOKIE_NAME,
  VERSION,
  COOKIE_SAME_SITE_MODE,
  COOKIE_LENGTH
} from '@/util/constants'

export class RootService extends CoreRootService {
  updateCookie
  setAuthenticated
  setSessionToken

  constructor () {
    super(submitRequest)

    const { setAuthenticated, setSessionToken } = useContext(RootContext)
    this.setAuthenticated = setAuthenticated
    this.setSessionToken = setSessionToken
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, updateCookie] = useCookie(COOKIE_NAME)
    this.updateCookie = updateCookie
  }

  private deleteCookie (): void {
    // deleteCookie didn't work always
    this.updateCookie('', {
      expires: DateTime.now().minus(COOKIE_LENGTH).toJSDate(),
      sameSite: COOKIE_SAME_SITE_MODE,
      domain: window.location.hostname || APP_DOMAIN
    })
  }

  async isSignUpEnabled (): Promise<boolean> {
    let response: ISignUpEnabledResponse
    try {
      response = await submitRequest<ISignUpEnabledResponse>(
        'get',
        'v1/user/sign-up-enabled'
      )
    } catch (e: any) {
      throw new Error(e)
    }
    return response.enabled
  }

  async sendContact (message: string, route: string): Promise<boolean> {
    let response: IBaseResponse
    try {
      response = await submitRequest<IBaseResponse>(
        'post',
        'v1/contact',
        { message, route, version: VERSION }
      )
    } catch (e: any) {
      throw new Error(e)
    }
    return response.success
  }

  async sendFeedback (message: string, route: string): Promise<boolean> {
    let response: IBaseResponse
    try {
      response = await submitRequest<IBaseResponse>(
        'post',
        'v1/feedback',
        { message, route, version: VERSION }
      )
    } catch (e: any) {
      throw new Error(e)
    }
    return response.success
  }

  async removeSession (): Promise<boolean> {
    let response: IBaseResponse
    try {
      response = await submitRequest<IBaseResponse>(
        'delete',
        'v1/user/auth/session'
      )
    } catch (e: any) {
      throw new Error(e)
    }
    return response.success
  }

  async getUser (): Promise<IUser> {
    let response: IGetUserResponse
    try {
      response = await submitRequest<IGetUserResponse>(
        'get',
        'v1/user/auth/session'
      )
    } catch (e: any) {
      if (e.message.includes('maintenance mode')) {
        window.location.href = `${window.location.origin}/error#maintenance`
        throw e
      }
      if (AUTHENTICATION_ERRORS.some(str => e.message.includes(str))) {
        this.deleteCookie()
        this.setAuthenticated(false)
        this.setSessionToken(null)
      }
      throw e
    }
    if (response.success) {
      return response.user
    } else {
      throw new Error(response.message)
    }
  }

  async getStatus (): Promise<IStatus> {
    let response: IStatusResponse
    try {
      response = await submitRequest<IStatusResponse>('get', 'v1/user/status')
    } catch (e: any) {
      if (e.message.includes('Unable to validate subscriptions')) {
        window.location.href = `${window.location.origin}/error#subscription`
        throw e
      }
      if (e.message.includes('maintenance mode')) {
        window.location.href = `${window.location.origin}/error#maintenance`
        throw e
      }
      if (AUTHENTICATION_ERRORS.some(str => e.message.includes(str))) {
        this.deleteCookie()
        this.setAuthenticated(false)
        this.setSessionToken(null)
      }
      throw e
    }
    return response.status
  }
}
